import type { NextConfig } from 'next';
import * as path from 'path';

export interface NextBunDockerOptions {
  /**
   * Additional path aliases to add to webpack config
   * @default {}
   */
  aliases?: Record<string, string>;
  
  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
  
  /**
   * Custom detection logic for Bun environment
   * @default auto-detect based on runtime and env vars
   */
  detectBun?: () => boolean;
}

/**
 * Detects if running in a Bun environment
 */
function isBunEnvironment(): boolean {
  return (
    process.env.DOCKER_BUILD === 'true' ||
    process.env.BUN_RUNTIME === 'true' ||
    process.argv[0]?.includes('bun') ||
    !!process.versions?.bun ||
    false
  );
}

/**
 * Next.js plugin to fix Bun + Docker build issues
 * 
 * @example
 * ```js
 * // next.config.js
 * const { withBunDocker } = require('next-bun-docker');
 * 
 * module.exports = withBunDocker({
 *   // your next config
 * });
 * ```
 */
export function withBunDocker(
  nextConfig: NextConfig = {},
  options: NextBunDockerOptions = {}
): NextConfig {
  const {
    aliases = {},
    debug = false,
    detectBun = isBunEnvironment
  } = options;

  return {
    ...nextConfig,
    webpack: (config, context) => {
      // Call the original webpack function if it exists
      if (typeof nextConfig.webpack === 'function') {
        config = nextConfig.webpack(config, context);
      }

      // Only apply fixes in Bun environment
      if (detectBun()) {
        if (debug) {
          console.log('🔧 next-bun-docker: Applying Bun+Docker fixes to webpack config');
        }

        // Disable symlinks resolution for Docker compatibility
        config.resolve = config.resolve || {};
        config.resolve.symlinks = false;

        // Fix TypeScript path aliases
        const { dir, isServer } = context;
        
        // Default aliases for common Next.js patterns
        const defaultAliases: Record<string, string> = {
          '@': path.resolve(dir, 'src'),
          '@/components': path.resolve(dir, 'src/components'),
          '@/lib': path.resolve(dir, 'src/lib'),
          '@/utils': path.resolve(dir, 'src/utils'),
          '@/styles': path.resolve(dir, 'src/styles'),
          '@/types': path.resolve(dir, 'src/types'),
          '@/hooks': path.resolve(dir, 'src/hooks'),
          '@/app': path.resolve(dir, 'src/app'),
          '@/pages': path.resolve(dir, 'src/pages'),
        };

        // Merge with existing aliases
        config.resolve.alias = {
          ...config.resolve.alias,
          ...defaultAliases,
          ...aliases,
        };

        // Ensure proper module resolution
        config.resolve.extensions = config.resolve.extensions || [
          '.ts',
          '.tsx',
          '.js',
          '.jsx',
          '.json',
        ];

        // Add fallback for Node.js built-ins if needed
        if (!isServer) {
          config.resolve.fallback = {
            ...config.resolve.fallback,
            fs: false,
            path: false,
            crypto: false,
          };
        }

        if (debug) {
          console.log('🔧 next-bun-docker: Webpack config updated successfully');
          console.log('   - Symlinks disabled');
          console.log('   - Path aliases configured');
          console.log('   - Module resolution optimized');
        }
      }

      return config;
    },
  };
}

// Default export for convenience
export default withBunDocker;